import NewBookingSummaryModal from '@/components/common/modals/new-booking-summary-modal/NewBookingSummaryModal';
import FormCampusVeranoWindsurfarea from '@/components/forms/form-campus-verano-windsurfarea/FormCampusVeranoWindsurfarea';
import { TForm as TFormCampusVeranoWindsurfarea } from '@/components/forms/form-campus-verano-windsurfarea/FormCampusVeranoWindsurfarea.types';
import FormEventoWingfoilWindsurfarea from '@/components/forms/form-evento-wingfoil-windsurfarea/FormEventoWingfoilWindsurfarea';
import { TForm as TFormEventoWingfoilWindsurfarea } from '@/components/forms/form-evento-wingfoil-windsurfarea/FormEventoWingfoilWindsurfarea.types';
import FormParticipantDataAccordion from '@/components/forms/form-participant-data-accordion/FormParticipantDataAccordion';
import type { TForm } from '@/components/forms/form-participant-data-accordion/FormParticipantDataAccordion.types';
import FormParticipantDataVp from '@/components/forms/form-participant-data-vp/FormParticipantDataVp';
import { Skeleton } from '@/components/ui/skeleton';
import useCreateSale from '@/hooks/queries/sales-api/useCreateSale';
import { useGetUserByEmail } from '@/hooks/queries/users-api/useGetUserByEmail';
import { getAgeRange, VEPCodes } from '@/lib/utils';
import { TOrganizationFAQ } from '@/services/organization';
import { TServiceDivision } from '@/services/servicesDivision';
import { TUsers } from '@/services/user';
import { useRootStore } from '@/stores/root-store';
import type { StepService } from '@/stores/slices/step-service/step-service.types';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import type ParticipantsDataStepProp from './ParticipantsDataStep.types';

interface RenderFormProps {
  stepServicesData: StepService | null;
  onSubmitCampuVeranoWindsurfarea: (
    data: TFormCampusVeranoWindsurfarea
  ) => void;
  onSubmitEventoWingfoilWindsurfarea: (
    data: TFormEventoWingfoilWindsurfarea
  ) => void;
  isPending: boolean;
  isUserPending: boolean;
  organization: TOrganizationFAQ;
  user: TUsers | undefined;
  onSubmitAdditionalData: (data: any) => void;
  setEmailUser: React.Dispatch<React.SetStateAction<string>>;
  emailUser: string;
  onSubmit: (data: TForm) => void;
}

const RenderForm: React.FC<RenderFormProps> = ({
  stepServicesData,
  onSubmitCampuVeranoWindsurfarea,
  onSubmitEventoWingfoilWindsurfarea,
  isPending,
  isUserPending,
  organization,
  user,
  onSubmitAdditionalData,
  setEmailUser,
  emailUser,
  onSubmit
}) => {
  const isCampusNauticoVerano =
    stepServicesData?.service?.code === 'campus-nautico-verano';
  const isEventoWingfoil =
    stepServicesData?.service?.code === 'evento-wingfoil';

  if (isCampusNauticoVerano) {
    return (
      <FormCampusVeranoWindsurfarea
        onSubmit={onSubmitCampuVeranoWindsurfarea}
        isPending={isPending}
      />
    );
  }
  if (isEventoWingfoil) {
    return (
      <FormEventoWingfoilWindsurfarea
        onSubmit={onSubmitEventoWingfoilWindsurfarea}
        isPending={isPending}
      />
    );
  }

  if (isUserPending)
    return (
      <div>
        <Skeleton className="w-full h-5" />
        <Skeleton className="w-full h-5 mt-1" />
        <Skeleton className="w-full h-20 mt-5" />
        <Skeleton className="w-full h-8 mt-5" />
      </div>
    );

  if (VEPCodes.includes(organization.code)) {
    return (
      <FormParticipantDataVp
        user={user}
        organization={organization}
        isPending={Boolean(isPending)}
        onSubmit={onSubmitAdditionalData}
      />
    );
  }

  return (
    <FormParticipantDataAccordion
      user={user}
      setEmailUser={setEmailUser}
      emailUser={emailUser}
      isPending={Boolean(isPending)}
      onSubmit={onSubmit}
    />
  );
};

const ParticipantsDataStep: React.FC<ParticipantsDataStepProp> = ({
  organization
}) => {
  const stepServicesData: StepService | null = useRootStore(
    (store) => store.stepServicesData
  );
  const loadStepServicesData = useRootStore(
    (store) => store.loadStepServicesData
  );

  const [openSummaryModal, setOpenSummaryModal] = useState(false);

  // check if user already exsist
  const [emailUser, setEmailUser] = useState('');
  const {
    data: user,
    isFetching: isUserPending,
    refetch: checkUserEmail,
    isError,
    error
  } = useGetUserByEmail(emailUser);

  // post
  const { mutate, isPending } = useCreateSale();

  const onSubmit = (data: TForm) => {
    // parse birthdate to dateISOString
    const participantsData = data.participants.map((participant) => {
      const { id, ...rest } = participant;
      if (id) return id;
      return {
        ...rest,
        birthdate: participant.birthdate.toISOString()
      };
    });

    const userParticipantData = {
      ...data.user,
      birthdate: data.user.birthdate ? data.user.birthdate.toISOString() : '', // parte date to ISO string
      isOwnUser: true // add value to indicate de user is participant
    };

    delete (userParticipantData as { userIsParticipant?: boolean })
      .userIsParticipant; // the end-point does not support this value

    const consumers = data.user.userIsParticipant
      ? [userParticipantData, ...participantsData]
      : participantsData;

    loadStepServicesData({
      email: data.user.email,
      phone: data.user?.phone ? data.user?.phone : undefined,
      firstName: data.user?.firstName,
      lastName: data.user?.lastName,
      identification: data?.user?.identification,
      consumers,
      paymentType: 'ef75aed8-40fb-489f-963c-11722aa12440'
    });

    setOpenSummaryModal(true);
  };

  const onSubmitAdditionalData = (data: any) => {
    const group = getAgeRange(data.user.birthdate);
    const { holidayDateDetails, ...restUser } = data.user;
    const { user, ...restData } = data;

    const consumers = [
      {
        ...restUser,
        group: `group-${group}`,
        holidayDate: data.user.holidayDate ? holidayDateDetails : '',
        ...restData,
        user: {
          ...restUser,
          holidayDate: data.user.holidayDate ? holidayDateDetails : ''
        }
      }
    ];

    const userData = data.parents[0];

    const submitValue = {
      email: userData?.email,
      phone: userData?.phone ?? undefined,
      firstName: userData?.firstName,
      lastName: userData?.lastName,
      identification: userData?.identification,
      consumers,
      paymentType: 'ef75aed8-40fb-489f-963c-11722aa12440',
      service: (stepServicesData as StepService).service?.id
    };

    mutate(submitValue);
  };

  const onSubmitCampuVeranoWindsurfarea = (
    data: TFormCampusVeranoWindsurfarea
  ) => {
    const {
      user,
      parent,
      student,
      acceptDataPrivacy,
      acceptTakePhotos,
      authOfferProducts
    } = data;
    const serviceDivisionGroups =
      stepServicesData?.serviceDivisionGroups as Partial<TServiceDivision>[];

    const studentParsed = {
      ...student,
      birthdate: student.birthdate.toISOString()
    };

    loadStepServicesData({
      email: user.email,
      phone: user.phone,
      firstName: user.firstName,
      lastName: user.lastName,
      identification: user?.identification,
      serviceDivisionGroups,
      consumers: [
        {
          ...studentParsed,
          group: null,
          parents: [user, parent],
          acceptDataPrivacy,
          acceptTakePhotos,
          authOfferProducts
        }
      ],
      paymentType: 'ef75aed8-40fb-489f-963c-11722aa12440'
    });
    setOpenSummaryModal(true);
  };

  const onSubmitEventoWingfoilWindsurfarea = (
    data: TFormEventoWingfoilWindsurfarea
  ) => {
    const serviceDivisionGroups =
      stepServicesData?.serviceDivisionGroups as Partial<TServiceDivision>[];
    const {
      userIsParticipant,
      user,
      emergencyContact,
      participant,
      acceptDataPrivacy,
      acceptTakePhotos,
      authOfferProducts
    } = data;

    const consumers = userIsParticipant
      ? [
          {
            ...user,
            birthdate: user.birthdate?.toISOString(),
            acceptDataPrivacy,
            acceptTakePhotos,
            authOfferProducts,
            isOwnUser: true,
            emergencyContact
          }
        ]
      : [
          {
            ...participant,
            birthdate: participant?.birthdate?.toISOString(),
            acceptDataPrivacy,
            acceptTakePhotos,
            authOfferProducts,
            emergencyContact,
            parents: [user]
          }
        ];

    loadStepServicesData({
      email: user.email,
      phone: user.phone,
      firstName: user.firstName,
      lastName: user.lastName,
      identification: user.identification,
      serviceDivisionGroups,
      consumers,
      paymentType: 'ef75aed8-40fb-489f-963c-11722aa12440'
    });
    setOpenSummaryModal(true);
  };

  useEffect(() => {
    if (emailUser) {
      checkUserEmail();
    }
  }, [emailUser, checkUserEmail]);

  useEffect(() => {
    if (isError) {
      if (error.status === 404) {
        toast.warning(t('toast.userNotFound'));
      }
    }
  }, [isError, error]);

  return (
    <>
      {openSummaryModal && (
        <NewBookingSummaryModal
          user={user}
          open={openSummaryModal}
          onOpenChange={setOpenSummaryModal}
          organization={organization}
          isPending={Boolean(isPending)}
          onSubmit={() => {
            const submitValue = stepServicesData as StepService;
            delete submitValue?.participants; // remove unnecessary value for the submit

            const serviceDivisionGroupsIds = submitValue?.serviceDivisionGroups
              ?.map((group) => group.id)
              ?.filter((id): id is string => id !== undefined);
            mutate({
              ...submitValue,
              service: submitValue?.service?.id,
              serviceDivisionGroups: serviceDivisionGroupsIds
            });
          }}
        />
      )}
      <RenderForm
        stepServicesData={stepServicesData}
        onSubmitCampuVeranoWindsurfarea={onSubmitCampuVeranoWindsurfarea}
        onSubmitEventoWingfoilWindsurfarea={onSubmitEventoWingfoilWindsurfarea}
        isPending={isPending}
        isUserPending={isUserPending}
        organization={organization}
        user={user}
        onSubmitAdditionalData={onSubmitAdditionalData}
        setEmailUser={setEmailUser}
        emailUser={emailUser}
        onSubmit={onSubmit}
      />
    </>
  );
};

export default ParticipantsDataStep;
